import React from 'react';
import styled from 'styled-components';
import { 
  Hospital,
  Database,
  Cloud,
  Code
} from 'lucide-react';

const AboutSection = styled.section`
  padding: 5rem 0;
  background-color: #ffffff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled.div`
  h2 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 1.5rem;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }
`;

const ExperienceCard = styled.div`
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 10px;
  border-left: 4px solid #ff6900;

  h3 {
    color: #333;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  p {
    color: #666;
    line-height: 1.6;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const SkillCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-3px);
  }

  .icon {
    font-size: 2.5rem;
    color: #ff6900;
    margin-bottom: 1rem;
  }

  h4 {
    color: #333;
    margin: 0;
    font-size: 1.1rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  padding: 2rem 0;
  border-top: 1px solid #eee;
`;

const StatItem = styled.div`
  text-align: center;

  .number {
    font-size: 2.5rem;
    font-weight: bold;
    color: #ff6900;
  }

  .label {
    color: #666;
    font-size: 1rem;
    margin-top: 0.5rem;
  }
`;

const About: React.FC = () => {
  const skills = [
    { icon: <Code className="icon" />, name: "Python/SQL" },
    { icon: <Cloud className="icon" />, name: "AWS/Azure" },
    { icon: <Database className="icon" />, name: "Data Warehouses" },
    { icon: <Cloud className="icon" />, name: "Apache Spark" },
    { icon: <Hospital className="icon" />, name: "Healthcare Systems" },
    { icon: <Database className="icon" />, name: "ETL/ELT Pipelines" }
  ];

  return (
    <AboutSection id="about">
      <Container>
        <AboutGrid>
          <AboutContent>
            <h2>About RJSpark</h2>
            <p>
              We are a specialized data engineering consultancy with deep expertise in healthcare 
              data systems and enterprise data architecture. Our mission is to help organizations 
              unlock the full potential of their data through robust, scalable, and compliant solutions.
            </p>
            <p>
              With extensive experience in healthcare data engineering, we understand the unique 
              challenges of working with sensitive medical data, regulatory requirements, and the 
              critical importance of data accuracy in healthcare decision-making.
            </p>

            <ExperienceCard>
              <h3>
                <Hospital /> Healthcare Data Engineering Expertise
              </h3>
              <p>
                Proven track record in developing HIPAA-compliant data pipelines, integrating 
                electronic health records (EHR/EMR), building healthcare analytics platforms, 
                and implementing data governance frameworks for medical organizations.
              </p>
            </ExperienceCard>
          </AboutContent>

          <div>
            <SkillsGrid>
              {skills.map((skill, index) => (
                <SkillCard key={index}>
                  {skill.icon}
                  <h4>{skill.name}</h4>
                </SkillCard>
              ))}
            </SkillsGrid>

            <StatsContainer>
              <StatItem>
                <div className="number">5+</div>
                <div className="label">Years Experience</div>
              </StatItem>
              <StatItem>
                <div className="number">50+</div>
                <div className="label">Projects Delivered</div>
              </StatItem>
              <StatItem>
                <div className="number">100%</div>
                <div className="label">HIPAA Compliant</div>
              </StatItem>
              <StatItem>
                <div className="number">24/7</div>
                <div className="label">Support Available</div>
              </StatItem>
            </StatsContainer>
          </div>
        </AboutGrid>
      </Container>
    </AboutSection>
  );
};

export default About;